import { useEffect, useState } from 'react';
import { Instagram, ArrowUpRight, Play } from 'lucide-react';

// Auto-updating Instagram feed, rendered in our own theme.
// Pulls Behold's JSON feed (no white third-party widget) and lays the
// latest posts out in a bento grid. Falls back to a themed follow-mosaic
// when the account has no posts yet or the feed can't be reached.
const BEHOLD_FEED_ID = 'rzHDNuH7akxIeDuclrCA';
const FEED_URL = `https://feeds.behold.so/${BEHOLD_FEED_ID}`;
const MAX_POSTS = 6;

const CAPTION_MAX = 90;
const pickCaption = (post) => {
    const raw = post.prunedCaption || post.caption || '';
    const clean = raw.replace(/#[\w]+/g, '').replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    return clean.length > CAPTION_MAX
        ? clean.slice(0, CAPTION_MAX).trimEnd() + '…'
        : clean;
};

const pickImage = (post) => {
    const s = post.sizes || {};
    return (
        s.large?.mediaUrl ||
        s.full?.mediaUrl ||
        s.medium?.mediaUrl ||
        s.small?.mediaUrl ||
        post.mediaUrl ||
        post.thumbnailUrl
    );
};

const FollowMosaic = ({ profileUrl, handle }) => (
    <a
        href={profileUrl}
        target="_blank"
        rel="noreferrer"
        data-testid="instagram-feed-placeholder"
        data-cursor="hover"
        className="group grid gap-2 md:gap-3 h-full min-h-[150px]"
        style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gridAutoRows: '1fr',
        }}
        aria-label={`Open ${handle} on Instagram`}
    >
        {Array.from({ length: 6 }).map((_, i) => (
            <span
                key={i}
                className="relative min-h-[120px] rounded-[14px] border border-[var(--line)] overflow-hidden flex items-center justify-center transition-colors duration-500 group-hover:border-[var(--accent)]"
                style={{
                    background:
                        i % 2 === 0
                            ? 'linear-gradient(135deg, color-mix(in srgb, var(--accent) 12%, var(--surface)) 0%, var(--surface) 70%)'
                            : 'linear-gradient(135deg, var(--surface) 0%, color-mix(in srgb, var(--ink) 6%, var(--surface)) 100%)',
                }}
            >
                <Instagram
                    size={22}
                    className="text-[var(--ink-3)] group-hover:text-[var(--accent)] transition-colors duration-500"
                />
                {i === 2 && (
                    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--ink-2)]">
                        {handle}
                        <ArrowUpRight size={11} />
                    </span>
                )}
            </span>
        ))}
    </a>
);

export const InstagramFeed = ({ profileUrl, handle }) => {
    const [posts, setPosts] = useState(null); // null = loading

    useEffect(() => {
        let alive = true;
        fetch(FEED_URL)
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then((data) => {
                if (alive) setPosts(Array.isArray(data.posts) ? data.posts : []);
            })
            .catch(() => alive && setPosts([]));
        return () => {
            alive = false;
        };
    }, []);

    // No posts yet (or feed unreachable) → themed follow prompt.
    if (posts !== null && posts.length === 0) {
        return <FollowMosaic profileUrl={profileUrl} handle={handle} />;
    }

    // Loading skeleton (matches grid so there's no layout shift).
    if (posts === null) {
        return (
            <div
                className="grid gap-2 md:gap-3 h-full min-h-[150px]"
                style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                    gridAutoRows: '1fr',
                }}
            >
                {Array.from({ length: MAX_POSTS }).map((_, i) => (
                    <span
                        key={i}
                        className="min-h-[120px] rounded-[14px] border border-[var(--line)] bg-[var(--surface)] animate-pulse"
                    />
                ))}
            </div>
        );
    }

    // Live posts → our own themed grid (fills the full column height).
    return (
        <div
            data-testid="instagram-live-feed"
            className="grid gap-2 md:gap-3 h-full min-h-[150px]"
            style={{
                gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                gridAutoRows: '1fr',
            }}
        >
            {posts.slice(0, MAX_POSTS).map((post, i) => (
                <a
                    key={post.id}
                    href={post.permalink || profileUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`instagram-post-${i}`}
                    data-cursor="hover"
                    className="group relative min-h-[120px] rounded-[14px] overflow-hidden border border-[var(--line)] hover:border-[var(--accent)] transition-colors duration-500 bg-[var(--surface)]"
                >
                    <img
                        src={pickImage(post)}
                        alt={(post.caption || 'Instagram post').slice(0, 90)}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {(post.mediaType === 'VIDEO' ||
                        post.mediaType === 'CAROUSEL_ALBUM') && (
                        <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/45 text-white backdrop-blur-sm">
                            <Play size={11} className="ml-[1px]" />
                        </span>
                    )}
                    <span className="absolute inset-0 flex flex-col justify-end gap-1 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-black/75 via-black/25 to-transparent">
                        {pickCaption(post) && (
                            <span className="font-sans-brand text-[9.5px] leading-tight text-white/90 line-clamp-2">
                                {pickCaption(post)}
                            </span>
                        )}
                        <Instagram size={13} className="text-white shrink-0" />
                    </span>
                </a>
            ))}
        </div>
    );
};

export default InstagramFeed;
